import { useParams } from 'react-router-dom';
import { useRepresentativesModal } from 'pages/Representatives/context/RepresentativesModalProvider';
import { useQueryClient } from 'react-query';
import { REPRESENTATIVES_QUERY_STRING } from 'shared/constants/REPRESENTATIVES_QUERY_STRING';
import { CircularProgress, Typography } from '@material-ui/core';
import { memo, useEffect } from 'react';
import { profileService } from 'shared/services/profileService';
import { IProfileMediaSetSelectPayload } from 'shared/interfaces/IProfile';
import { FileType } from 'shared/enums/FileType';
import { EPhotoEditorConsumer } from 'shared/enums/EPhotoEditorConsumer';
import { useGetPhotoEditorMutates } from '../../hooks/useGetPhotoEditorMutates';
import { useGetPhotoEditorData } from '../../hooks/useGetPhotoEditorData';
import { isJsonString } from 'shared/utils/common';
import { OPENING_ERROR_MESSAGE, SAVING_ERROR_MESSAGE } from '../../const/PHOTO_EDITOR_MESSAGE';
import AsyncPhotoEditorV2 from 'components/AsyncPhotoEditorV2/AsyncPhotoEditorV2';
import DialogInfo from 'components/DialogInfo/DialogInfo';

const { setProfilePrimaryImage } = profileService();

type PhotoEditorV2ProviderWrapperProps = {
  showEditor: boolean;
  handleEditor: VoidFunction;
  imageFiles: File[];
  consumerType: EPhotoEditorConsumer;
};

const PhotoEditorV2ProviderWrapper = ({
  showEditor,
  handleEditor,
  imageFiles,
  consumerType,
}: PhotoEditorV2ProviderWrapperProps) => {
  const queryClient = useQueryClient();

  const invalidateSideInfoProfile = async (id: string) => {
    queryClient.invalidateQueries([REPRESENTATIVES_QUERY_STRING.representatives_single_profile, id]);
  };

  const invalidateMultiplesProfile = async () => {
    queryClient.invalidateQueries([REPRESENTATIVES_QUERY_STRING.representatives_multiple_profile], {
      refetchInactive: true,
    });
  };

  const invalidateHiddenImage = async (id: string) => {
    await queryClient.invalidateQueries(
      [REPRESENTATIVES_QUERY_STRING.representatives_single_profile_hidden_media, id, FileType.IMAGE],
      {
        refetchInactive: true,
      },
    );
  };
  const invalidateAttachedImage = async (id: string) => {
    await queryClient.invalidateQueries(
      [REPRESENTATIVES_QUERY_STRING.representatives_single_profile_attached_media, id, FileType.IMAGE],
      {
        refetchInactive: true,
      },
    );
  };

  const { id: profileId } = useParams() as { id: string };

  // POST request when save button is clicked
  const {
    patchMultipleMediaAsync,
    patchMediaTagsAsync,
    setSelectProfileMediaMutateAsync,
    bindToProfileMutate,
    uploadMediaMutateAsync,
    updateSkillAsync,
    unselectMutate,
    multipleMediaError,
    ProfileMediaMutateError,
    bindToProfileError,
    UploadMediaError,
    updateSkillError,
  } = useGetPhotoEditorMutates();

  // Get request data for Opening the Photo Editor, Skills, Media and Media Tags and others
  const {
    mediaData,
    mediaDataLoading,
    mediaDataError,
    skillsData,
    skillsDataLoading,
    skillDataError,
    mediaTags,
    fetchingTags,
    fetchingTagError,
    multipleProfilesLoading,
    multipleProfilesError,
    attached_images,
    selectedAgencies,
    unselectedAgencies,
    agencies,
    setSelectedAgencies,
  } = useGetPhotoEditorData(profileId);

  // Bind To profile use in uploadMediaCallback
  const bindToProfile = async (payload: IProfileMediaSetSelectPayload, imgID: string, profileID?: string) => {
    await bindToProfileMutate(
      {
        profileId: profileID || profileId,
        payload: { ...payload, sort: attached_images ? attached_images.length : 0 },
      },
      {
        onError: () => {
          handleSetMessage(OPENING_ERROR_MESSAGE.header, OPENING_ERROR_MESSAGE.message, 'Close');
          handleEditor();
          handleOpen();
        },
      },
    );
  };

  // Function for saving to other profile
  const uploadMediaCallback = async (data: any, agencies: any) => {
    if (consumerType === EPhotoEditorConsumer.PROFILE_MEDIA_EDITOR) return;
    try {
      if (data && agencies.length) {
        const { id } = data.data;
        const promises = agencies
          .filter((agency: any) => agency.id !== profileId)
          .map(async (agency: any) => {
            return await bindToProfile(
              {
                id,
                tag_list: 'image',
              },
              id,
              agency.id,
            );
          });
        await Promise.all(promises);
      }
    } catch (error) {
      handleSetMessage(OPENING_ERROR_MESSAGE.header, OPENING_ERROR_MESSAGE.message, 'Close');
      handleEditor();
      handleOpen();
    }
  };

  // Function to open the error modal
  const { handleOpen, handleSetMessage } = useRepresentativesModal();
  const { mutate: primaryImageMutate } = setProfilePrimaryImage();

  // Error Handling when opening the PhotoEditor
  useEffect(() => {
    if (skillDataError || mediaDataError || fetchingTagError || multipleProfilesError) {
      handleSetMessage(OPENING_ERROR_MESSAGE.header, OPENING_ERROR_MESSAGE.message, 'Close');
      handleEditor();
      handleOpen();
    }
  }, [skillDataError, mediaDataError, fetchingTagError, multipleProfilesError]);

  // Error Handling after clcking the save button, the onSuccess and onError is happening inside the MFE, this is temporary for error handling
  useEffect(() => {
    if (updateSkillError || multipleMediaError || UploadMediaError || ProfileMediaMutateError || bindToProfileError) {
      handleEditor();
      handleSetMessage(SAVING_ERROR_MESSAGE.header, SAVING_ERROR_MESSAGE.message, 'Close');
      handleOpen();
    }
  }, [updateSkillError, multipleMediaError, UploadMediaError, ProfileMediaMutateError, bindToProfileError]);

  // Common Props used in PhotoEditor in EditProfile
  const common_photoeditor_props = {
    patchMultipleMediaAsync,
    patchMediaTagsAsync,
    uploadMediaMutateAsync,
    setSelectProfileMediaMutateAsync,
    updateSkillAsync,
    variant: 'default',
    mediaData: mediaData?.data,
    mediaTags: mediaTags?.data,
    images: imageFiles,
    enableUndo: true,
    featureEnableRenderSkillTags: true,
    skillsData: skillsData?.data,
    profileId: profileId,
  };

  const agency_props = {
    uploadMediaCallback,
    selectedAgencies,
    setSelectedAgencies,
    agencies,
  };

  // Photo Editor Props, will improve this more in the future
  const photoEditorV2Props: any = {
    // The side_info props is based on the SideInfo.tsx
    [EPhotoEditorConsumer.PROFILE_SIDE_INFO]: {
      ...common_photoeditor_props,
      ...agency_props,
      setAsPrimary: true, // This is used for changing the primary image
      // Saving process
      onSuccessImageUpdate: () => {
        queryClient.invalidateQueries(['talents/skills']);
      },
      onSuccessImageUpload: (data: any) => {
        queryClient.invalidateQueries(['talents/skills']);
        if (imageFiles.length === 1) {
          const formData = new FormData();
          formData.set('medium_id', data.data.id);
          primaryImageMutate(
            { profileId: profileId, formData },
            {
              onSuccess: async () => {
                await queryClient.invalidateQueries(['media']);

                invalidateSideInfoProfile(profileId);
                invalidateMultiplesProfile();
                invalidateAttachedImage(profileId);
                // Attached profile thats been selected invalidate them too
                unselectedAgencies?.forEach((selected: any) => {
                  invalidateHiddenImage(selected.id);
                });
                // Invalidate the UnSelected Profile but invalidate the hidden media
                await selectedAgencies.forEach((selected: any) => {
                  invalidateAttachedImage(selected.id);
                });
              },
              onError: () => {
                handleSetMessage('Request failed', 'Changing your primary image is not available for now', 'Close');
                handleOpen();
              },
            },
          );
        }
      },
    },
    // Props that is needed for Media/Documents PhotoEditor
    [EPhotoEditorConsumer.PROFILE_MEDIA_IMAGE]: {
      ...common_photoeditor_props,
      ...agency_props,
      // Saving Process
      onSuccessImageUpdate: async (data: any) => {
        await queryClient.invalidateQueries(['talents/skills']);
        await queryClient.invalidateQueries(['media']);
      },
      onSuccessImageUpload: async () => {
        // Invalidate the UnSelected Profile but invalidate the hidden media
        unselectedAgencies.forEach((selected: any) => {
          invalidateHiddenImage(selected.id);
        });
        // Attached profile thats been selected invalidate them too
        selectedAgencies.forEach((selected: any) => {
          invalidateAttachedImage(selected.id);
        });
        await queryClient.invalidateQueries(['talents/skills']);
        await invalidateAttachedImage(profileId);
      },
    },
    [EPhotoEditorConsumer.PROFILE_MEDIA_EDITOR]: {
      ...common_photoeditor_props,

      onSuccessImageUpdate: async () => {
        queryClient.invalidateQueries([REPRESENTATIVES_QUERY_STRING.representatives_single_profile, profileId]);

        await invalidateAttachedImage(profileId);
        await invalidateHiddenImage(profileId);
        await queryClient.invalidateQueries(['talents/skills']);
        await queryClient.invalidateQueries(['media']);

        if (imageFiles.length === 1) {
          if (isJsonString(imageFiles[0].name)) {
            const { isCroppedAttachmentNull, isPrimary } = JSON.parse(imageFiles[0].name);
            if (isCroppedAttachmentNull) {
              const { id } = JSON.parse(imageFiles[0].name);
              unselectMutate(
                { profileId: profileId, profileMediaId: id },
                {
                  onSuccess: () => {
                    invalidateAttachedImage(profileId);
                  },
                  onError: () => {
                    handleSetMessage('Request failed', 'Some issues found when editing your image', 'Close');
                    handleOpen();
                  },
                },
              );
            }
          }
        }
      },
    },
  };

  if (skillsDataLoading || mediaDataLoading || fetchingTags || multipleProfilesLoading) {
    return (
      <DialogInfo
        openDialog={true}
        handleDialog={() => {
          // Abort if they cancel while opening the photoEditor
          queryClient.cancelQueries(['talents/skills']);
          queryClient.cancelQueries(['media']);
          queryClient.cancelQueries(['media_tags']);
          queryClient.cancelQueries([REPRESENTATIVES_QUERY_STRING.representatives_multiple_profile]);
          handleEditor();
        }}
        title=""
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 10,
            flexDirection: 'column',
          }}
        >
          <CircularProgress size={18} />
          <Typography>Photo Editor opening...</Typography>
        </div>
      </DialogInfo>
    );
  }

  return showEditor && mediaTags && mediaData && skillsData && imageFiles ? (
    <AsyncPhotoEditorV2 show={showEditor} close={() => handleEditor()} {...photoEditorV2Props[consumerType]} />
  ) : null;
};

export default memo(PhotoEditorV2ProviderWrapper);
